/**
 * usePipelineReconcile.ts
 *
 * Data hook for /sources/reconcile (Pipeline Reconcile dashboard).
 * SYSTEM_ADMIN only — server enforces via requireRole.
 */
import { useState, useCallback } from 'react';
import { apiFetch } from '@/lib/apiFetch';

export interface JurisdictionReconcileRow {
  jurisdiction_id: string;
  jurisdiction_name: string;
  jurisdiction_path: string | null;
  no_extracted_text: number;
  extracted_no_chunks: number;
  chunks_not_embedded: number;
  /** Agendas with 0 attendees — expected (blank pre-meeting roll-call), never "stuck". */
  agenda_no_attendees: number;
  /** Minutes with 0 attendees, not yet retry-capped — the real recall gap ("stuck"). */
  minutes_no_attendees: number;
  /** Minutes with 0 attendees that hit the retry cap — needs manual review, never re-requeued. */
  minutes_no_attendees_needs_review: number;
  /** Attempt-count histogram for minutes_no_attendees: { "0", "1", "2", "3+" } */
  minutes_retry_breakdown: Record<string, number>;
  /** Minutes with 0 motions, not yet retry-capped — worth a look (can legitimately be 0 for workshops). */
  minutes_no_motions: number;
  /** Minutes with 0 motions that hit the retry cap — needs manual review, never re-requeued. */
  minutes_no_motions_needs_review: number;
  /** Attempt-count histogram for minutes_no_motions: { "0", "1", "2", "3+" } */
  motions_retry_breakdown: Record<string, number>;
  /** Minutes with 0 financial actions, not yet retry-capped — worth a look (can legitimately be 0 if no spending discussed). */
  minutes_no_financial: number;
  /** Minutes with 0 financial actions that hit the retry cap — needs manual review, never re-requeued. */
  minutes_no_financial_needs_review: number;
  /** Attempt-count histogram for minutes_no_financial: { "0", "1", "2", "3+" } */
  financial_retry_breakdown: Record<string, number>;
  jobs_pending: number;
  jobs_running: number;
  jobs_running_past_timeout: number;
  jobs_failed: number;
  needs_review_artifacts: number;
}

export interface PipelineReconcileData {
  jurisdictions: JurisdictionReconcileRow[];
  nullJurisdictionArtifacts: number;
  runningTimeoutMinutes: number;
  refreshedAt: string;
}

export interface ReconciliationRunRow {
  id: string;
  mode: 'preview' | 'run';
  triggered_by: string | null;
  findings: Record<string, number>;
  requeued: Record<string, number>;
  created_at: string;
}

export interface AttendeeCoverageInfo {
  agendaNoAttendeesCount: number;
  minutesNoAttendeesNeedsReviewCount: number;
  minutesRetryBreakdown: Record<string, number>;
}

export interface MotionsCoverageInfo {
  minutesNoMotionsNeedsReviewCount: number;
  minutesRetryBreakdown: Record<string, number>;
}

export interface FinancialCoverageInfo {
  minutesNoFinancialNeedsReviewCount: number;
  minutesRetryBreakdown: Record<string, number>;
}

export interface ReconcileActionResult {
  runId: string | null;
  createdAt: string;
  wouldRequeue?: Record<string, number>;
  found?: Record<string, number>;
  requeued?: Record<string, number>;
  attendeeCoverage?: AttendeeCoverageInfo;
  motionsCoverage?: MotionsCoverageInfo;
  financialCoverage?: FinancialCoverageInfo;
}

export interface DedupResult {
  redundant: Record<string, number>;
  duplicates: Record<string, number>;
  total: number;
}

export function usePipelineReconcile() {
  const [data, setData] = useState<PipelineReconcileData | null>(null);
  const [runs, setRuns] = useState<ReconciliationRunRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const [runsError, setRunsError] = useState<string | null>(null);

  // Action state (preview/run)
  const [actionLoading, setActionLoading] = useState<
    'preview' | 'run' | 'dedupe' | null
  >(null);
  const [actionResult, setActionResult] =
    useState<ReconcileActionResult | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const [dedupResult, setDedupResult] = useState<DedupResult | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch('/api/pipeline-reconcile');
      if (!res.ok) {
        if (res.status === 403)
          throw new Error('Forbidden — SYSTEM_ADMIN access required');
        throw new Error(`pipeline-reconcile ${res.status}`);
      }
      const json = (await res.json()) as { data: PipelineReconcileData };
      setData(json.data ?? null);
      setLastRefreshed(new Date());
    } catch (e) {
      setError(
        e instanceof Error ? e.message : 'Failed to load pipeline reconcile'
      );
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshRuns = useCallback(async () => {
    setRunsError(null);
    try {
      const res = await apiFetch('/api/pipeline-reconcile/runs');
      if (!res.ok) {
        setRunsError(`Failed to load runs (${res.status})`);
        return;
      }
      const json = (await res.json()) as { data: ReconciliationRunRow[] };
      setRuns(json.data ?? []);
    } catch (e) {
      setRunsError(e instanceof Error ? e.message : 'Failed to load runs');
    }
  }, []);

  const previewReconcile = useCallback(async () => {
    setActionLoading('preview');
    setActionError(null);
    setActionResult(null);
    try {
      const res = await apiFetch('/api/pipeline-reconcile/preview', {
        method: 'POST',
      });
      if (!res.ok) throw new Error(`preview ${res.status}`);
      const json = (await res.json()) as { data: ReconcileActionResult };
      setActionResult(json.data);
      void refreshRuns();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Preview failed');
    } finally {
      setActionLoading(null);
    }
  }, [refreshRuns]);

  const runReconcile = useCallback(async () => {
    setActionLoading('run');
    setActionError(null);
    setActionResult(null);
    try {
      const res = await apiFetch('/api/pipeline-reconcile/run', {
        method: 'POST',
      });
      if (!res.ok) throw new Error(`run ${res.status}`);
      const json = (await res.json()) as { data: ReconcileActionResult };
      setActionResult(json.data);
      void refreshRuns();
      void refresh();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Run failed');
    } finally {
      setActionLoading(null);
    }
  }, [refresh, refreshRuns]);

  const removeDedupes = useCallback(async () => {
    setActionLoading('dedupe');
    setActionError(null);
    setDedupResult(null);
    try {
      const res = await apiFetch('/api/pipeline-reconcile/remove-dedupes', {
        method: 'POST',
      });
      if (!res.ok) throw new Error(`remove-dedupes ${res.status}`);
      const json = (await res.json()) as { data: DedupResult };
      setDedupResult(json.data);
      void refresh();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Dedup failed');
    } finally {
      setActionLoading(null);
    }
  }, [refresh]);

  return {
    data,
    runs,
    loading,
    error,
    runsError,
    lastRefreshed,
    refresh,
    refreshRuns,
    previewReconcile,
    runReconcile,
    actionLoading,
    actionResult,
    actionError,
    removeDedupes,
    dedupResult,
  };
}
